// Shared health/status badges for list pages.
//
// - CountBadge: a numeric pill whose color reflects the status of the items it
//   counts (good=green, warn=amber, bad=red, neutral=gray).
// - HealthPill: a subscriber-level rollup (healthy/degraded/down/na).
// - toneFromUpTotal: maps an (up, total) pair to a CountBadge tone.

const TONE_CLASSES = {
  good:    'bg-green-100 text-green-700',
  warn:    'bg-amber-100 text-amber-700',
  bad:     'bg-red-100 text-red-700',
  neutral: 'bg-gray-100 text-gray-500',
};

// up = items in the desired state (registered / online / assigned / active).
// total = items that could be. Returns a CountBadge tone. `unknown` (up===null)
// means we don't have live data yet → neutral.
export function toneFromUpTotal(up, total) {
  if (!total) return 'neutral';
  if (up == null) return 'neutral';
  if (up >= total) return 'good';
  if (up <= 0) return 'bad';
  return 'warn';
}

export function CountBadge({ count = 0, tone = 'neutral', title }) {
  const cls = TONE_CLASSES[tone] || TONE_CLASSES.neutral;
  return (
    <span
      title={title}
      className={`inline-flex min-w-[1.5rem] justify-center rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}
    >
      {count}
    </span>
  );
}

const HEALTH_META = {
  healthy:  { cls: 'bg-green-100 text-green-700', dot: 'bg-green-500', label: 'Healthy' },
  degraded: { cls: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', label: 'Degraded' },
  down:     { cls: 'bg-red-100 text-red-700',     dot: 'bg-red-500',   label: 'Down' },
  na:       { cls: 'bg-gray-100 text-gray-400',   dot: 'bg-gray-300',  label: '—' },
};

// health: 'healthy' | 'degraded' | 'down' | 'na'
export function HealthPill({ health = 'na', title }) {
  const m = HEALTH_META[health] || HEALTH_META.na;
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${m.cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
}

// Roll up an (up, total) endpoint tally into a health verdict.
export function healthFromUpTotal(up, total) {
  if (!total) return 'na';
  if (up == null) return 'na';
  if (up >= total) return 'healthy';
  if (up <= 0) return 'down';
  return 'degraded';
}
